import JSZip from "jszip";
import { downloadFile } from "./helpers";
import { certificateName } from "../../shared/utils/certificateNaming";

/**
 * Create a ZIP file with multiple files and trigger download
 * @param {Array<{filename: string, content: string}>} files - Array of {filename, content} objects
 * @param {string} zipFilename - Name of the ZIP file to download
 * @returns {Promise<void>}
 * @throws {Error} "Failed to create ZIP file" if ZIP CONSTRUCTION fails ;
 *   a failure in the download stage after a successful build propagates
 *   with its ORIGINAL message instead (the two are separate failure
 *   domains : see the try/catch comment in the function body)
 *
 * @example
 * const files = [
 *   { filename: "example-com-csr-20251206-103045.txt", content: "CSR data..." },
 *   { filename: "example-com-privatekey-20251206-103045.txt", content: "Private key data..." }
 * ];
 * await downloadAsZip(files, "example-com-csr-bundle-20251206-103045.zip");
 */
export async function downloadAsZip(files, zipFilename) {
  if (!files || files.length === 0) {
    throw new Error("No files provided for ZIP creation");
  }

  if (!zipFilename) {
    throw new Error("ZIP filename is required");
  }

  // Generation and download are separate failure domains : the
  // try/catch below wraps only ZIP CONSTRUCTION, so a downloadFile
  // throw (after a successful build) propagates as itself instead of
  // being relabelled "Failed to create ZIP file". Today the benefit
  // is DIAGNOSTIC only : both callers (CSRGenerator handleDownloadZip,
  // ChainAnalyzer handleDownloadAllZip) deliberately catch either and
  // show one fixed customer sentence, so the distinction
  // reaches logs and debugging, not the customer ; per-cause customer
  // wording would be an operator call.
  let zipBlob;
  try {
    // Create new ZIP instance
    const zip = new JSZip();

    // Validate and filter files before adding to ZIP
    const validFiles = files.filter(({ filename, content }) => {
      if (!filename || content === undefined || content === null) {
        console.warn("Skipping file with missing filename or content:", {
          filename,
          hasContent: content !== undefined && content !== null,
        });
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      throw new Error("No valid files to add to ZIP");
    }

    // Calculate total size before adding files (security check)
    let totalSize = 0;
    const maxZipSize = 50 * 1024 * 1024; // 50MB limit
    const fileSizes = [];

    for (const { filename, content } of validFiles) {
      const contentSize =
        typeof content === "string"
          ? new Blob([content]).size
          : content.length || 0;
      totalSize += contentSize;
      fileSizes.push({ filename, size: contentSize });

      if (totalSize > maxZipSize) {
        const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
        const largestFiles = fileSizes
          .sort((a, b) => b.size - a.size)
          .slice(0, 3)
          .map(
            (f) => `${f.filename} (${(f.size / (1024 * 1024)).toFixed(2)}MB)`
          )
          .join(", ");

        throw new Error(
          `Total file size (${totalSizeMB}MB) exceeds maximum allowed (50MB). ` +
            `Largest files: ${largestFiles}. ` +
            `Please reduce the number of files or their sizes.`
        );
      }

      zip.file(filename, content);
    }

    // Generate ZIP blob
    zipBlob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: {
        level: 6, // Standard compression (balance between size and speed)
      },
    });
  } catch (error) {
    console.error("Error creating ZIP file:", error);
    // Re-throw validation errors with original message
    if (
      error.message.includes("No valid files") ||
      error.message.includes("exceeds maximum allowed")
    ) {
      throw error;
    }
    // Wrap other errors with generic message
    throw new Error("Failed to create ZIP file");
  }

  // Download through the shared helper, which passes an already-built
  // Blob through BY IDENTITY (see the downloadFile JSDoc) : no second
  // in-memory copy at up to the 50MB ceiling above. No mimeType
  // argument : the helper ignores it for Blob content, and JSZip
  // already stamped this Blob application/zip at generateAsync. This
  // was the last hand-rolled anchor click outside
  // helpers.js:downloadFile (2026-08-07 download/copy consistency
  // pass, operator-directed).
  downloadFile(zipBlob, zipFilename);
}

/**
 * Create a ZIP bundle for CSR and Private Key downloads
 * @param {string} csr - Certificate Signing Request content
 * @param {string} privateKey - Private Key content
 * @param {string} commonName - Domain or e-mail from CSR
 * @param {Function} generateStandardFilename - Filename generation function
 * @param {Function} sanitizeFilename - Filename sanitization function
 * @param {Function} generateTimestamp - Timestamp generation function
 * @returns {Promise<void>}
 */
export async function downloadCSRBundle(
  csr,
  privateKey,
  commonName,
  generateStandardFilename,
  sanitizeFilename,
  generateTimestamp
) {
  const sanitized = sanitizeFilename(commonName);
  const timestamp = generateTimestamp();

  const files = [
    {
      filename: generateStandardFilename(commonName, "csr", "txt"),
      content: csr,
    },
    {
      filename: generateStandardFilename(commonName, "privatekey", "txt"),
      content: privateKey,
    },
  ];

  const zipFilename = `${sanitized}-csr-bundle-${timestamp}.zip`;
  await downloadAsZip(files, zipFilename);
}

/**
 * Create a ZIP bundle for Certificate chain downloads
 * @param {Object} result - Chain analysis result containing certificates and validation
 * @param {Function} generateStandardFilename - Filename generation function
 * @param {Function} sanitizeFilename - Filename sanitization function
 * @param {Function} generateTimestamp - Timestamp generation function
 * @returns {Promise<void>}
 */
export async function downloadChainBundle(
  result,
  generateStandardFilename,
  sanitizeFilename,
  generateTimestamp
) {
  // Bare name : a filename cannot carry the "+ N More SANs" suffix.
  const domain = certificateName(result.certificate) || "certificate";
  const sanitized = sanitizeFilename(domain);
  const timestamp = generateTimestamp();

  const files = [];

  // Add each Certificate in chain. Every one of them, including any
  // the page marked as not part of the chain : seeing what you
  // actually supplied is the point of the per-certificate files.
  //
  // NAMED FROM WHAT THE CERTIFICATE IS, NOT FROM ITS POSITION
  // (2026-08-19 review round 17). The last entry was called "root" on
  // position alone, so a spare authority concatenated onto a bundle
  // was filed as <domain>-root.txt, and so was a root this tool
  // resolved from a store rather than one the customer had.
  // NO TWO MEMBERS MAY SHARE A NAME (2026-08-19 review round 19). The
  // extras arm below makes the extras unique, but the LINKED run has no
  // such guarantee : sortChain links on Common Name text, and a
  // self-signed certificate's issuer name IS its subject name, so a
  // second self-signed certificate carrying the same name is linked
  // rather than marked, typed root like the first, and filed under the
  // same name. This file's own store index treats several roots sharing
  // a subject as a real shape. The archive is keyed by name, so a
  // collision does not warn - it silently drops a certificate.
  const usedNames = new Set();
  result.chain.forEach((cert, index) => {
    // EXTRAS ARE NAMED FROM THE MARK, so the names stay unique
    // (2026-08-19 review round 18). Naming from `type` alone dropped
    // the uniqueness that naming from POSITION had by construction :
    // sortChain types every entry by self-signedness, extras included,
    // so a bundle carrying a spare self-signed authority - which is
    // the commonest spare there is - had TWO entries typed root. The
    // archive is keyed by filename, so the second write displaced the
    // first and the customer's real root was not in it at all.
    const certType =
      index === 0
        ? "certificate"
        : cert.notPartOfChain
          ? `extra-${index}`
          : cert.type === "root"
            ? "root"
            : `intermediate-${index}`;
    let filename = generateStandardFilename(domain, certType, "txt");
    if (usedNames.has(filename)) {
      filename = generateStandardFilename(
        domain,
        `${certType}-${index}`,
        "txt"
      );
    }
    usedNames.add(filename);
    files.push({ filename, content: cert.certificate });
  });

  // Add full chain PEM. Extras are excluded here, and ONLY here,
  // because this is the file the customer deploys - the same rule the
  // download and the copy button follow.
  const pemContent = result.chain
    .filter((cert) => !cert.notPartOfChain)
    .map((cert) => cert.certificate)
    .join("\n");
  files.push({
    filename: generateStandardFilename(domain, "chain", "pem"),
    content: pemContent,
  });

  // Add analysis JSON
  const exportData = {
    timestamp: new Date().toISOString(),
    certificate: result.certificate,
    chain: result.chain,
    validation: result.validation,
  };
  files.push({
    filename: generateStandardFilename(domain, "analysis", "json"),
    content: JSON.stringify(exportData, null, 2),
  });

  const zipFilename = `${sanitized}-chain-bundle-${timestamp}.zip`;
  await downloadAsZip(files, zipFilename);
}
